<template>
  <div class="previous-claim-form__container" :class="styleClass">
    <!-- no claims heading -->
    <div class="px-4 py-2 px-lg-7 limedSpruce">
      <span class="white--text font-weight-bold">Claim Details</span>
    </div>

    <!-- no claims form -->
    <div class="px-4 py-9 px-lg-9 concrete">
      <!-- 
        the disabled prop excludes a nested ValidationObserver from the parent 
        ValidationObserver allowing independent form validation 
      -->
      <ValidationObserver
        ref="observerFormPreviousClaims"
        v-slot="{ passes, reset, invalid }"
        disabled
      >
        <form @submit.prevent="onSubmit(passes, reset, invalid)">
          <template v-if="isShowField('PreviousClaimClaimedUnder')">
            <BaseLabel text="Claimed under" class="mb-2" />
            <BaseSelect
              v-model="form.previousClaimClaimedUnder"
              dataUiid="PreviousClaimClaimedUnder"
              :className="hideDetails ? 'mb-10' : 'mb-3'"
              :hideDetails="hideDetails"
              :items="
                setItemDataUiids(
                  'PreviousClaimClaimedUnder',
                  PREVIOUS_CLAIM_CLAIMED_UNDER_ITEMS
                )
              "
              :returnObject="true"
              :rules="{
                required: {
                  required: true,
                  fieldLabel: 'Claimed under',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              aria-label="Claimed under"
            />
          </template>

          <template v-if="isShowField('PreviousClaimTypeOfLoss')">
            <BaseLabel
              :text="
                $vuetify.breakpoint.lgAndUp ? 'Type of claim' : 'Claim type'
              "
              class="mb-2"
            />
            <BaseSelect
              v-model="form.previousClaimTypeOfLoss"
              dataUiid="PreviousClaimTypeOfLoss"
              :hideDetails="hideDetails"
              :className="hideDetails ? 'mb-10' : 'mb-3'"
              :items="
                setItemDataUiids(
                  'PreviousClaimTypeOfLoss',
                  previousClaimTypeOfLossOptions
                )
              "
              :returnObject="true"
              :rules="{
                required: {
                  required: true,
                  fieldLabel: 'Type of claim',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              aria-label="Type of claim"
            />
          </template>

          <template v-if="isShowField('PreviousClaimDateOfLoss')">
            <BaseLabel
              :text="
                $vuetify.breakpoint.lgAndUp ? 'Date of claim' : 'Claim date'
              "
              class="mb-2"
            />
            <BaseInputDate
              ref="previousClaimDateOfLoss"
              v-model="form.previousClaimDateOfLoss"
              dataUiid="PreviousClaimDateOfLoss"
              :styleClass="hideDetails ? 'mb-10' : 'mb-3'"
              :hideDetails="hideDetails"
              :rules="{
                required: {
                  required: true,
                  fieldLabel: 'Date of claim',
                },
                date_valid: {
                  errorMessageTemplate: 'Date of claim is not valid.',
                },
                regex: {
                  pattern: '^\\s*$|^(\\d{2})\\/(\\d{2})\\/\\d{4}$',
                  customMessage: 'Date of claim is not valid.',
                },
                date_in_range_past: {
                  max: 5,
                  units: 'years',
                  customMessage:
                    'Sorry! Date of claim must be a date within the last 5 years.',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            />
          </template>

          <template v-if="isShowField('PreviousClaimAmountOfLoss')">
            <BaseLabel
              :text="
                $vuetify.breakpoint.lgAndUp
                  ? 'Cost of claim (£)'
                  : 'Claim value'
              "
              class="mb-2"
            />
            <BaseInput
              ref="previousClaimAmountOfLoss"
              v-model="form.previousClaimAmountOfLoss"
              :value="form.previousClaimAmountOfLoss"
              prefix="£"
              moneyFormat
              :className="hideDetails ? 'mb-10' : 'mb-3'"
              dataUiid="PreviousClaimAmountOfLoss"
              :hideDetails="hideDetails"
              :rules="{
                required: {
                  required: true,
                  fieldLabel: 'Cost of claim',
                },
                regex: {
                  required: true,
                  pattern: '^[0-9,]+(.[0-9]{1,2})?$',
                  customMessage:
                    'Only numbers up to two decimal places are allowed in this field.',
                },
                max_currency_length: {
                  length: 10,
                  fieldLabel: 'Cost of claim',
                  errorMessageTemplate:
                    '{0} (£) may not be greater than 10 characters.',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '260px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              aria-label="Cost of claim (£)"
            />
          </template>

          <BaseButton
            :width="$vuetify.breakpoint.lgAndUp ? '226px' : '100%'"
            dataUiid="AddItemButton - Previous Claim"
            depressed
            rounded
            color="psdarkgreen"
            text="ADD"
            height="44"
            class="font-20 font-weight-bold white--text"
            :class="hideDetails ? 'mt-8' : 'mt-3'"
            type="submit"
          />
        </form>
      </ValidationObserver>
    </div>

    <!-- no claims table -->
    <template v-if="tableItems.length">
      <!-- desktop -->
      <BaseTable
        v-if="$vuetify.breakpoint.xl"
        :table-headers="tableHeaders"
        :table-items="tableItems"
        :table-actions="tableActions"
        @deleteItem="deleteItem"
      />

      <!-- mobile -->
      <template v-else>
        <div
          v-for="(item, index) in tableItems"
          :key="index"
          class="mt-4 text-center"
        >
          <v-card outlined class="pa-4">
            <div class="d-flex flex-column claim-data__mobile-display">
              <div
                class="claim-infor__section d-flex justify-space-between align-center mb-4"
              >
                <v-chip
                  color="psdarkgreen"
                  text-color="white"
                  class="font-weight-bold claim-under"
                  v-if="isShowField('PreviousClaimClaimedUnder')"
                  >{{ item.PreviousClaimClaimedUnder.Text }}</v-chip
                >
                <span
                  v-if="isShowField('PreviousClaimDateOfLoss')"
                  class="font-weight-bold font-20 primary--text claim-date"
                  >{{ item.PreviousClaimDateOfLoss.Text }}</span
                >
              </div>
              <div
                class="claim-detail__section d-flex justify-space-between align-center"
              >
                <span
                  v-if="isShowField('PreviousClaimTypeOfLoss')"
                  class="font-weight-bold font-20 primary--text text-left pr-4 claim-type"
                  >{{ item.PreviousClaimTypeOfLoss.Text }}</span
                >
                <span
                  v-if="isShowField('PreviousClaimAmountOfLoss')"
                  class="font-weight-bold font-20 primary--text text-right claim-amount"
                  >{{
                    item.PreviousClaimAmountOfLoss.Value | formatCurrency
                  }}</span
                >
              </div>
            </div>
          </v-card>

          <BaseButton
            v-if="tableActions?.delete"
            depressed
            flat
            small
            link
            icon="mdi-minus-circle"
            iconSize="large"
            iconColor="error"
            text="REMOVE"
            class="px-0 mt-4"
            @click="deleteItem(index)"
          />
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { ValidationObserver } from "vee-validate";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import {
  setItemDataUiids,
  PREVIOUS_CLAIM_CLAIMED_UNDER_ITEMS,
  PREVIOUS_CLAIM_TYPE_OF_LOSS_ITEMS,
} from "@/constants/component.constants.js";
import BaseLabel from "@/components/BaseComponents/BaseLabel";
import BaseSelect from "@/components/BaseComponents/BaseSelect";
import BaseButton from "@/components/BaseComponents/BaseButton";
import BaseInput from "@/components/BaseComponents/BaseInput";
import BaseInputDate from "@/components/BaseComponents/BaseInputDate";
import BaseTable from "@/components/BaseComponents/BaseTable";
import { scrollToFirstErrorInput } from "@/common/utils";

export default {
  name: "PreviousClaimsForm",
  components: {
    ValidationObserver,
    BaseLabel,
    BaseSelect,
    BaseButton,
    BaseInput,
    BaseInputDate,
    BaseTable,
  },
  props: {
    hideDetails: {
      type: [Boolean, String],
      default: "auto",
    },
    styleClass: {
      type: String,
      default: "",
    },
    tableHeaders: {
      type: Array,
      default: () => [
        {
          text: "Claimed under",
          value: "PreviousClaimClaimedUnder.Text",
          align: "start",
          sortable: false,
          interfaceKey: "PreviousClaimClaimedUnder",
        },
        {
          text: "Type of claim",
          value: "PreviousClaimTypeOfLoss.Text",
          align: "start",
          sortable: false,
          interfaceKey: "PreviousClaimTypeOfLoss",
        },
        {
          text: "Date of claim",
          value: "PreviousClaimDateOfLoss.Text",
          align: "start",
          sortable: false,
          interfaceKey: "PreviousClaimDateOfLoss",
        },
        {
          text: "Cost of claim (£)",
          value: "PreviousClaimAmountOfLoss.Text",
          sortable: false,
          interfaceKey: "PreviousClaimAmountOfLoss",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
        },
      ],
    },
    previousClaimTypeOfLossOptions: {
      type: Array,
      default: () => PREVIOUS_CLAIM_TYPE_OF_LOSS_ITEMS,
    },
  },
  data() {
    return {
      setItemDataUiids,
      PREVIOUS_CLAIM_CLAIMED_UNDER_ITEMS,
      PREVIOUS_CLAIM_TYPE_OF_LOSS_ITEMS,

      repeatingGroupPreviousClaim: [],

      form: {
        previousClaimClaimedUnder: null,
        previousClaimTypeOfLoss: null,
        previousClaimDateOfLoss: null,
        previousClaimAmountOfLoss: null,
      },
      tableItems: [],
      tableActions: {
        delete: true,
      },
      visibilityStore: {},
    };
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, ["repeatingQuestionSetIndex"]),
    ...mapState(QUESTIONS_MODULE, ["questionMap"]),
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, ["increaseRepeatingQuestionSetIndex"]),
    onSubmit(passes, reset, invalid) {
      passes(() => {
        // set repeating index
        this.increaseRepeatingQuestionSetIndex();

        // format table items
        const formattedTableItems = {
          PreviousClaimClaimedUnder: {
            Value: this.form.previousClaimClaimedUnder?.value,
            Text: this.form.previousClaimClaimedUnder?.text,
            RepeatingQuestionSetIndex: this.repeatingQuestionSetIndex,
            InterfaceKey: "PreviousClaimClaimedUnder",
          },
          PreviousClaimTypeOfLoss: {
            Value: this.form.previousClaimTypeOfLoss?.value,
            Text: this.form.previousClaimTypeOfLoss?.text,
            RepeatingQuestionSetIndex: this.repeatingQuestionSetIndex,
            InterfaceKey: "PreviousClaimTypeOfLoss",
          },
          PreviousClaimDateOfLoss: {
            Value: this.form.previousClaimDateOfLoss,
            Text: this.form.previousClaimDateOfLoss,
            RepeatingQuestionSetIndex: this.repeatingQuestionSetIndex,
            InterfaceKey: "PreviousClaimDateOfLoss",
          },
          PreviousClaimAmountOfLoss: {
            Value: this.form.previousClaimAmountOfLoss,
            Text: this.$options.filters.formatCurrency(
              this.form.previousClaimAmountOfLoss
            ),
            RepeatingQuestionSetIndex: this.repeatingQuestionSetIndex,
            InterfaceKey: "PreviousClaimAmountOfLoss",
          },
        };

        // add table items
        this.addItem(formattedTableItems);

        // reset form
        const keys = Object.keys(this.form);

        keys.forEach((k) => {
          this.form[k] = null;
        });
        this.$refs.previousClaimDateOfLoss.resetFieldAll();

        //reset validation
        this.$nextTick(() => {
          reset();
        });
      });
      if (invalid) {
        setTimeout(() => {
          scrollToFirstErrorInput(this.$refs.observerFormPreviousClaims);
        }, 0);
      }
    },
    addItem(formattedTableItems) {
      this.tableItems.push(formattedTableItems);

      this.$emit("add-table-items", this.tableItems);
    },
    deleteItem(rowIndex) {
      this.tableItems = this.tableItems.filter(
        (item, index) => index !== rowIndex
      );

      this.$emit("remove-table-items", this.tableItems);
    },
    isShowField(interfaceKey) {
      if (!(interfaceKey in this.visibilityStore)) {
        this.visibilityStore[interfaceKey] = this.tableHeaders.find(
          (item) => item.interfaceKey === interfaceKey
        );
      }
      return this.visibilityStore[interfaceKey];
    },
  },
  watch: {
    "repeatingGroupPreviousClaim.Value"(value) {
      this.tableItems = value;
    },
  },
  mounted() {
    this.repeatingGroupPreviousClaim =
      this.questionMap.RepeatingGroupPreviousClaim;
  },
};
</script>

<style lang="scss" scoped>
.v-card.theme--light.v-sheet--outlined {
  border: solid 1px var(--v-webgrey-base) !important;
}
.claim-form-without-claim-under {
  .claim-data__mobile-display {
    .claim-detail__section {
      flex-direction: column !important;
      align-items: start !important;
      .claim-type {
        margin-bottom: 16px;
      }
    }
  }
}
</style>
